import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useMemo, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import appListIconUrl from '@/assets/admin-overview/app-list.svg'
import arrowRightIconUrl from '@/assets/admin-overview/arrow-right.svg'
import departmentsIconUrl from '@/assets/admin-overview/departments.svg'
import modelDistributionIconUrl from '@/assets/admin-overview/model-distribution.svg'
import modelRegistryIconUrl from '@/assets/admin-overview/model-registry.svg'
import taskKeyIconUrl from '@/assets/admin-overview/task-key.svg'
import quickReviewIconUrl from '@/assets/admin-overview/quick-review.svg'
import taskReviewIconUrl from '@/assets/admin-overview/task-review.svg'
import { ErrorState } from '@/components/feedback/error-state'
import { Skeleton } from '@/components/feedback/skeleton'
import { PageHeader } from '@/components/layout/page-header'
import { Button } from '@/components/primitives/button'
import { formatDateTime } from '@/features/admin/shared/format'
import { AdminShellPage } from '@/features/admin/shell/shell-page'
import { useHubAgentName } from '@/features/shared/brand/branding-hooks'
import { cn } from '@/lib/cn'

import { getAdminOverview, type AdminOverviewData, type AdminOverviewRecentApp } from './api'

type AdminOverviewRoute =
  | '/admin/app-review'
  | '/admin/agent/hub'
  | '/admin/apps'
  | '/admin/members'
  | '/admin/model-keys'
  | '/admin/model-provider'
  | '/admin/organization/departments'
  | '/admin/settings/branding'
  | '/admin/settings/sso'
  | '/admin/idp/provider'

type TaskTone = 'danger' | 'info' | 'primary' | 'warning'

interface MetricCardModel {
  description: string
  label: string
  route: AdminOverviewRoute
  value: string
}

interface QuickEntryModel {
  icon: string
  iconClassName?: string
  label: string
  route: AdminOverviewRoute
}

interface OverviewTaskModel {
  detail: string
  icon: string
  iconClassName?: string
  id: string
  route: AdminOverviewRoute
  title: string
  tone: TaskTone
}

const numberFormatter = new Intl.NumberFormat('zh-CN')
const quickAppListIconClassName = 'size-[22px]'
const quickReviewIconClassName = 'size-[22px]'
const departmentIconClassName = 'h-5 w-[27px]'
const quickModelRegistryIconClassName = 'h-[23px] w-6'
const quickModelDistributionIconClassName = 'h-6 w-[21px]'

function formatCount(value: number) {
  return numberFormatter.format(value)
}

function relativeTime(value: string, fallback: string) {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return fallback
  }

  const diffMs = Date.now() - date.getTime()
  const minute = 60 * 1000
  const hour = 60 * minute
  const day = 24 * hour

  if (diffMs < minute) {
    return fallback
  }

  if (diffMs < hour) {
    return `${String(Math.max(1, Math.floor(diffMs / minute)))}m`
  }

  if (diffMs < day) {
    return `${String(Math.floor(diffMs / hour))}h`
  }

  return `${String(Math.floor(diffMs / day))}d`
}

function useOverviewModels(data: AdminOverviewData | undefined) {
  const { t } = useTranslation('adminOverview')
  const hubAgentName = useHubAgentName()

  return useMemo(() => {
    const metrics: MetricCardModel[] = [
      {
        description: t('metrics.pendingReview.description'),
        label: t('metrics.pendingReview.label'),
        route: '/admin/app-review',
        value: formatCount(data?.pendingReviewApps ?? 0),
      },
      {
        description: t('metrics.activeMembers.description'),
        label: t('metrics.activeMembers.label'),
        route: '/admin/members',
        value: formatCount(data?.activeMembers ?? 0),
      },
      {
        description: t('metrics.activeModels.description', { hubAgentName }),
        label: t('metrics.activeModels.label'),
        route: '/admin/model-provider',
        value: formatCount(data?.activeModels ?? 0),
      },
      {
        description: t('metrics.activeKeys.description'),
        label: t('metrics.activeKeys.label'),
        route: '/admin/model-keys',
        value: formatCount(data?.activeDistributionKeys ?? 0),
      },
    ]
    const quickEntries: QuickEntryModel[] = [
      {
        icon: appListIconUrl,
        iconClassName: quickAppListIconClassName,
        label: t('quickEntries.apps'),
        route: '/admin/apps',
      },
      {
        icon: quickReviewIconUrl,
        iconClassName: quickReviewIconClassName,
        label: t('quickEntries.review'),
        route: '/admin/app-review',
      },
      {
        icon: departmentsIconUrl,
        iconClassName: departmentIconClassName,
        label: t('quickEntries.departments'),
        route: '/admin/organization/departments',
      },
      {
        icon: modelRegistryIconUrl,
        iconClassName: quickModelRegistryIconClassName,
        label: t('quickEntries.modelProvider'),
        route: '/admin/model-provider',
      },
      {
        icon: modelDistributionIconUrl,
        iconClassName: quickModelDistributionIconClassName,
        label: t('quickEntries.modelKeys'),
        route: '/admin/model-keys',
      },
    ]
    const tasks: OverviewTaskModel[] = []

    if (!data) {
      return { metrics, quickEntries, tasks }
    }

    if (data.pendingReviewApps > 0) {
      tasks.push({
        detail: t('tasks.pendingReview.detail', { count: data.pendingReviewApps }),
        icon: taskReviewIconUrl,
        iconClassName: 'h-[18px] w-[17px]',
        id: 'pending-review',
        route: '/admin/app-review',
        title: t('tasks.pendingReview.title'),
        tone: 'warning',
      })
    }

    if (data.expiringDistributionKeys > 0) {
      tasks.push({
        detail: t('tasks.expiringKeys.detail', { count: data.expiringDistributionKeys }),
        icon: taskKeyIconUrl,
        iconClassName: 'h-5 w-[17.5px]',
        id: 'expiring-keys',
        route: '/admin/model-keys',
        title: t('tasks.expiringKeys.title'),
        tone: 'danger',
      })
    }

    if (data.activeModels === 0) {
      tasks.push({
        detail: t('tasks.noModels.detail', { hubAgentName }),
        icon: modelRegistryIconUrl,
        iconClassName: 'h-[19px] w-5',
        id: 'no-models',
        route: '/admin/model-provider',
        title: t('tasks.noModels.title'),
        tone: 'primary',
      })
    }

    if (data.activeDistributionKeys === 0) {
      tasks.push({
        detail: t('tasks.noKeys.detail'),
        icon: taskKeyIconUrl,
        iconClassName: 'h-5 w-[17.5px]',
        id: 'no-keys',
        route: '/admin/model-keys',
        title: t('tasks.noKeys.title'),
        tone: 'primary',
      })
    }

    if (data.enabledQuotaRules > 0) {
      tasks.push({
        detail: t('tasks.quotaRules.detail', { count: data.enabledQuotaRules }),
        icon: taskKeyIconUrl,
        iconClassName: 'h-5 w-[17.5px]',
        id: 'quota-rules',
        route: '/admin/model-keys',
        title: t('tasks.quotaRules.title'),
        tone: 'info',
      })
    }

    if (!data.ssoConfigured) {
      tasks.push({
        detail: t('tasks.sso.detail'),
        icon: departmentsIconUrl,
        iconClassName: departmentIconClassName,
        id: 'sso',
        route: '/admin/settings/sso',
        title: t('tasks.sso.title'),
        tone: 'info',
      })
    }

    if (!data.coreAgentEnabled) {
      tasks.push({
        detail: t('tasks.coreAgent.detail', { hubAgentName }),
        icon: modelRegistryIconUrl,
        iconClassName: 'h-[19px] w-5',
        id: 'core-agent',
        route: '/admin/agent/hub',
        title: t('tasks.coreAgent.title', { hubAgentName }),
        tone: 'info',
      })
    }

    if (!data.brandingComplete) {
      tasks.push({
        detail: t('tasks.branding.detail'),
        icon: appListIconUrl,
        iconClassName: 'size-5',
        id: 'branding',
        route: '/admin/settings/branding',
        title: t('tasks.branding.title'),
        tone: 'info',
      })
    }

    return { metrics, quickEntries, tasks }
  }, [data, hubAgentName, t])
}

function MetricCard({ metric, onNavigate }: { metric: MetricCardModel; onNavigate: () => void }) {
  return (
    <button
      className="grid min-h-[112px] cursor-pointer gap-3 rounded-lg border border-[#ececec] bg-white p-5 text-left transition-colors hover:bg-[#fbfcff] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
      onClick={onNavigate}
      type="button"
    >
      <span className="text-[15px] leading-5 tracking-[0.3px] text-[var(--color-text)]">
        {metric.label}
      </span>
      <span className="text-[30px] leading-8 font-bold text-[var(--color-primary)]">
        {metric.value}
      </span>
      <span className="text-[13px] leading-5 text-[rgba(26,28,25,0.65)]">{metric.description}</span>
    </button>
  )
}

function OverviewPanel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <section className={cn('rounded-lg border border-[#ececec] bg-white', className)}>
      {children}
    </section>
  )
}

function QuickEntry({ entry, onNavigate }: { entry: QuickEntryModel; onNavigate: () => void }) {
  return (
    <button
      className="grid min-w-[68px] cursor-pointer justify-items-center gap-2 rounded-md p-2 transition-colors hover:bg-[#f7f9ff] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
      onClick={onNavigate}
      type="button"
    >
      <span className="flex size-10 items-center justify-center rounded-[4px] bg-[rgba(25,91,255,0.05)]">
        <img
          alt=""
          aria-hidden
          className={cn('object-contain', entry.iconClassName)}
          src={entry.icon}
        />
      </span>
      <span className="max-w-[76px] text-center text-xs leading-4 text-[#242527]">
        {entry.label}
      </span>
    </button>
  )
}

function TaskIcon({
  icon,
  iconClassName,
  tone,
}: {
  icon: string
  iconClassName?: string
  tone: TaskTone
}) {
  const toneClassName: Record<TaskTone, string> = {
    danger: 'bg-[rgba(255,73,73,0.12)]',
    info: 'bg-[#e8efff]',
    primary: 'bg-[rgba(25,91,255,0.08)]',
    warning: 'bg-[#fff3cf]',
  }

  return (
    <span
      className={cn(
        'flex size-9 shrink-0 items-center justify-center rounded-lg',
        toneClassName[tone],
      )}
    >
      <img
        alt=""
        aria-hidden
        className={cn('object-contain', iconClassName ?? 'size-4')}
        src={icon}
      />
    </span>
  )
}

function TaskItem({ onNavigate, task }: { onNavigate: () => void; task: OverviewTaskModel }) {
  return (
    <button
      className="grid w-full cursor-pointer grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-4 border-b border-[#f0f0f0] py-3 text-left last:border-b-0 hover:bg-[#fbfcff] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-focus)]"
      onClick={onNavigate}
      type="button"
    >
      <TaskIcon icon={task.icon} iconClassName={task.iconClassName} tone={task.tone} />
      <span className="min-w-0">
        <span className="block truncate text-sm leading-5 text-[var(--color-text)]">
          {task.title}
        </span>
        <span className="mt-1 block truncate text-[13px] leading-5 text-[rgba(26,28,25,0.65)]">
          {task.detail}
        </span>
      </span>
      <img alt="" aria-hidden className="h-2.5 w-1.5" src={arrowRightIconUrl} />
    </button>
  )
}

function ActivityItem({
  app,
  emptyTimeLabel,
  isLast,
}: {
  app: AdminOverviewRecentApp
  emptyTimeLabel: string
  isLast: boolean
}) {
  const { t } = useTranslation('adminApps')
  const statusKey = `status.${app.status}`
  const statusLabel = t(statusKey)

  return (
    <li className="relative grid gap-1 pb-6 pl-6 last:pb-0">
      <span
        aria-hidden
        className="absolute left-[5px] top-[4px] size-[9px] rounded-full bg-[var(--color-primary)]"
      />
      {isLast ? null : (
        <span
          aria-hidden
          className="absolute bottom-0 left-[9px] top-[18px] w-px bg-[rgba(26,28,25,0.08)]"
        />
      )}
      <p className="truncate text-sm leading-5 text-[var(--color-text)]">
        {app.name}
        <span className="ml-1 text-[#8c8e90]">
          {statusLabel === statusKey ? app.status : statusLabel}
        </span>
      </p>
      <p className="truncate text-[13px] leading-5 text-[rgba(26,28,25,0.55)]">
        {formatDateTime(app.updatedAt)}
      </p>
      <p className="text-xs leading-4 text-[#929292]">
        {relativeTime(app.updatedAt, emptyTimeLabel)}
      </p>
    </li>
  )
}

function OverviewSkeleton() {
  return (
    <div className="grid gap-4">
      <div className="grid gap-4 xl:grid-cols-4">
        {Array.from({ length: 4 }, (_, index) => (
          <Skeleton className="h-28 rounded-lg" key={index} />
        ))}
      </div>
      <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.68fr)]">
        <div className="grid gap-4">
          <Skeleton className="h-36 rounded-lg" />
          <Skeleton className="h-64 rounded-lg" />
        </div>
        <Skeleton className="min-h-[340px] rounded-lg" />
      </div>
    </div>
  )
}

export function AdminOverviewPage() {
  const { t } = useTranslation('adminOverview')
  const navigate = useNavigate()
  const overviewQuery = useQuery({
    queryFn: getAdminOverview,
    queryKey: ['admin-overview'],
  })
  const { metrics, quickEntries, tasks } = useOverviewModels(overviewQuery.data)

  const goTo = (route: AdminOverviewRoute) => {
    void navigate({ to: route })
  }

  return (
    <AdminShellPage activePath="/admin/overview">
      <div className="flex h-full flex-col overflow-hidden bg-white">
        <PageHeader description={t('description')} title={t('title')} />
        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-6 lg:px-[30px]">
          {overviewQuery.isError ? (
            <div className="flex min-h-[360px] items-center justify-center">
              <ErrorState
                action={
                  <Button
                    className="mt-3"
                    onClick={() => {
                      void overviewQuery.refetch()
                    }}
                    size="sm"
                    variant="secondary"
                  >
                    {t('actions.retry')}
                  </Button>
                }
                description={t('error.description')}
                title={t('error.title')}
              />
            </div>
          ) : overviewQuery.isLoading ? (
            <OverviewSkeleton />
          ) : (
            <div className="grid gap-4">
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                {metrics.map((metric) => (
                  <MetricCard
                    key={metric.label}
                    metric={metric}
                    onNavigate={() => goTo(metric.route)}
                  />
                ))}
              </div>

              <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(320px,0.68fr)]">
                <div className="grid content-start gap-4">
                  <OverviewPanel className="p-5">
                    <h2 className="text-[15px] leading-5 font-medium text-[var(--color-text)]">
                      {t('quickEntries.title')}
                    </h2>
                    <div className="mt-5 flex flex-wrap gap-3">
                      {quickEntries.map((entry) => (
                        <QuickEntry
                          entry={entry}
                          key={entry.route}
                          onNavigate={() => goTo(entry.route)}
                        />
                      ))}
                    </div>
                  </OverviewPanel>

                  <OverviewPanel className="p-5">
                    <div className="flex items-center justify-between gap-4">
                      <h2 className="text-[15px] leading-5 font-medium text-[var(--color-text)]">
                        {t('tasks.title')}
                      </h2>
                      <span className="text-sm leading-5 text-[var(--color-text)]">
                        {t('tasks.count', { count: tasks.length })}
                      </span>
                    </div>
                    {tasks.length > 0 ? (
                      <div className="mt-5">
                        {tasks.map((task) => (
                          <TaskItem key={task.id} onNavigate={() => goTo(task.route)} task={task} />
                        ))}
                      </div>
                    ) : (
                      <div className="mt-5 rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-surface-muted)] p-5 text-sm text-[var(--color-text-muted)]">
                        {t('tasks.empty')}
                      </div>
                    )}
                  </OverviewPanel>
                </div>

                <OverviewPanel className="min-h-[360px] p-5">
                  <h2 className="text-[15px] leading-5 font-medium text-[var(--color-text)]">
                    {t('activity.title')}
                  </h2>
                  {overviewQuery.data?.recentApps.length ? (
                    <ol className="mt-6">
                      {overviewQuery.data.recentApps.map((app, index) => (
                        <ActivityItem
                          app={app}
                          emptyTimeLabel={t('activity.justNow')}
                          isLast={index === overviewQuery.data.recentApps.length - 1}
                          key={app.id}
                        />
                      ))}
                    </ol>
                  ) : (
                    <div className="mt-5 rounded-lg border border-dashed border-[var(--color-border)] bg-[var(--color-surface-muted)] p-5 text-sm text-[var(--color-text-muted)]">
                      {t('activity.empty')}
                    </div>
                  )}
                </OverviewPanel>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminShellPage>
  )
}
